/**
 * The Add Log Source wizard's chrome — everything on the page that is the same
 * whether the step has loaded, is loading, or blew up.
 *
 * **Eager**, and shared with the skeleton for the reason the Add Collector
 * wizard's frame is: the step behind it is `lazy()`, so a skeleton drawing its
 * own copy of the title, the rail, the card's explanation, the transport control
 * or the Advanced summary would be a second place for all of them to drift.
 *
 * Everything here is knowable without a server response, which is why it renders
 * for real in the loading state (P-05 §1). The one exception is the subtitle,
 * which names the Log Source Type and therefore waits on the catalog.
 */
import { SectionDescription, SectionTitle } from '@/features/app-shell/section'

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

import { LOG_SOURCE_TRANSPORTS } from '../../types'
import { WizardStepRail } from '../wizard-step-rail'

import type { LogSourceTransport } from '../../types'
import type { ReactNode } from 'react'

/**
 * The flow, in full, before it starts.
 *
 * Both steps are named from the first screen because the second is the one that
 * matters: registering a source only says where AIR® will listen, and a source
 * nothing has been pointed at yet is not a working source. Verify is where that
 * gets proven.
 */
const ADD_LOG_SOURCE_STEPS = ['Register source', 'Verify'] as const

/** The rail's step numbers, one-based, as it prints them. */
export const REGISTER_STEP = 1
export const VERIFY_STEP = 2

type AddLogSourceFrameProps = {
	/**
	 * The line under the title — `<Source type> · Network Logs · received via
	 * collector`. A node rather than a string so the skeleton can hand it a
	 * placeholder: it is the one part of this chrome that waits on the catalog.
	 */
	subtitle: ReactNode
	activeStep: number
	/** The step's body — its card, and anything beside it. */
	children: ReactNode
	/**
	 * The step's actions, **below** the card rather than in a `CardFooter` — they
	 * move the wizard, they do not act on the card's contents, and the design
	 * frames put them outside it for that reason.
	 */
	footer: ReactNode
}

export const AddLogSourceFrame = ({
	subtitle,
	activeStep,
	children,
	footer,
}: AddLogSourceFrameProps) => {
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="flex min-w-0 flex-col gap-1">
					<SectionTitle>Add network log source</SectionTitle>
					<SectionDescription>{subtitle}</SectionDescription>
				</div>

				<WizardStepRail steps={ADD_LOG_SOURCE_STEPS} activeStep={activeStep} />
			</div>

			<div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
				{children}

				<div className="flex items-center justify-end gap-3">{footer}</div>
			</div>
		</div>
	)
}

/**
 * The Register step's card, with the one sentence that says what this screen is
 * for.
 *
 * "Parsing is handled out of the box" is load-bearing rather than reassurance:
 * every other log platform makes the operator write a parser at this point, and
 * somebody who does not read it here will go looking for the parser field.
 */
export const RegisterSourceCard = ({ children }: { children: ReactNode }) => {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Register source</CardTitle>
				<CardDescription>
					Tell AIR® where to receive this source and on which collector. Parsing
					is handled out of the box.
				</CardDescription>
			</CardHeader>

			<CardContent className="flex flex-col gap-5">{children}</CardContent>
		</Card>
	)
}

type TransportToggleProps = {
	value?: LogSourceTransport
	onChange?: (transport: LogSourceTransport) => void
	/** The skeleton's variant: the two options are real, neither is choosable yet. */
	disabled?: boolean
}

/**
 * UDP or TCP — the whole vocabulary.
 *
 * No TLS option, and the caption says why: this leg of the journey is inside the
 * customer's own network, between a device and a Collector on the same site.
 * The hop that leaves the building is the Collector's, and that one is HTTPS.
 *
 * Options come from `LOG_SOURCE_TRANSPORTS`, so the control cannot offer a
 * transport a `LogSource` could not hold.
 */
export const TransportToggle = ({
	value,
	onChange,
	disabled,
}: TransportToggleProps) => {
	return (
		<ToggleGroup
			aria-label="Transport"
			variant="outline"
			className="w-full"
			value={value ? [value] : []}
			disabled={disabled}
			onValueChange={(next: string[]) => {
				// The primitive hands back bare strings, so the value is narrowed
				// against the vocabulary rather than asserted into it. Deselecting the
				// active option is refused: a source with no transport cannot listen.
				const selected = next[0]
				if (isTransport(selected) && selected !== value) onChange?.(selected)
			}}
		>
			{LOG_SOURCE_TRANSPORTS.map((transport) => (
				<ToggleGroupItem
					key={transport}
					value={transport}
					// The selected option takes the primary fill, as the design frame
					// draws it. The house `outline` toggle marks selection by going
					// *transparent*, which reads on a filter row carrying counts but
					// leaves a two-option picker with no visible answer to "which one
					// is set?". Same modifier as the base rule, so tailwind-merge
					// replaces it rather than racing it in the stylesheet.
					className="aria-pressed:bg-primary aria-pressed:text-primary-foreground h-9 flex-1"
				>
					{transport.toUpperCase()}
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	)
}

function isTransport(value: string | undefined): value is LogSourceTransport {
	return LOG_SOURCE_TRANSPORTS.some((transport) => transport === value)
}

type AdvancedSectionProps = {
	/** The three inputs. Only rendered once expanded, so the skeleton has none. */
	children?: ReactNode
	/** The skeleton's variant: the summary is real, there is nothing to open yet. */
	disabled?: boolean
}

/**
 * The settings almost nobody changes, behind one row.
 *
 * Collapsed by design, and the summary names all three so the row is worth
 * reading without opening it — an `Advanced` with nothing after it is a row
 * everybody has to open once to find out whether it matters.
 *
 * The summary lives here, in the eager module, because the skeleton renders it
 * for real; the inputs do not, because a collapsed accordion has nothing to
 * show and drawing placeholder fields inside a closed panel would be drawing
 * something nobody can see.
 */
export const AdvancedSection = ({
	children,
	disabled,
}: AdvancedSectionProps) => {
	return (
		<Accordion className="border-t">
			<AccordionItem value="advanced">
				<AccordionTrigger disabled={disabled} className="text-base font-normal">
					Advanced — message framing · allowed senders (CIDR) · expected source
					IP
				</AccordionTrigger>
				<AccordionContent>{children}</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}
